import React from "react";
import Badge from "../common/Badge";
import Card from "../common/Card";
import { XP_DISCLAIMER } from "../../config/supernovaXp";
import { METRIC } from "./supernovaSummary";
import {
  TIER_DISPLAY_ONLY_NOTE,
  TIER_UNKNOWN_NOTE,
  TIER_VALUATION_NOTE,
} from "./supernovaTiers";
import { fmtCount, fmtXp, plural } from "./supernovaFormat";

/**
 * "Reward tiers" — the four staked-count tiers and where the member sits on them.
 *
 * DISPLAY ONLY. Every figure comes from supernovaTiers (which reads
 * config/supernovaXp.TIER_BONUSES); this component renders them and nothing
 * else. TODO(backend, supervised): awarding a tier bonus is the staking engine's
 * job and its rules aren't settled — the token-valuation minimum in particular
 * (OQ-4) — so the track states that caveat instead of implying a credit. No code
 * path here adds a bonus to any balance.
 *
 * AN UNKNOWN STAKED COUNT DRAWS NO PROGRESS. With the stake read unconnected the
 * rail renders in its resting state, the pill says the count isn't connected, and
 * no tier is marked reached or unreached — because both are claims about a
 * position we can't see. The tiers themselves still show: knowing what they are
 * is useful even when we can't say where you stand.
 *
 * COMPLIANCE: tier bonuses are in-app points with no monetary value, stated
 * inline. Nothing on this surface is a return, yield, income, or a projection.
 *
 * @param {Object} props
 * @param {Object} props.track          buildTierTrack() result
 * @param {string} [props.stakedStatus] METRIC status of the staked count
 * @param {string|null} [props.stakedNote] why that count is missing
 */
const SupernovaTierTrack = ({ track, stakedStatus, stakedNote }) => {
  const loading = stakedStatus === METRIC.LOADING;
  /* No wallet connected is its own answer, and a different one from "the read
     isn't wired up" — the member can act on the first. */
  const idle = stakedStatus === METRIC.IDLE;

  return (
    <Card hover={false} data-testid="supernova-tier-track">
      <div className="flex items-baseline justify-between gap-2.5 flex-wrap">
        <h4 className="text-[15px] font-semibold text-arc-ink m-0">
          Reward tiers{" "}
          <span className="font-normal text-arc-ink-3 text-[12.5px]">
            — lump XP bonus when a tier is reached
          </span>
        </h4>
        {track.known ? (
          <Badge tone="accent" data-testid="supernova-tier-pill">
            {track.currentLabel}
          </Badge>
        ) : (
          <Badge tone="mock" title={stakedNote || TIER_UNKNOWN_NOTE} data-testid="supernova-tier-pill">
            {loading
              ? "Reading your stake…"
              : idle
              ? "Connect a wallet"
              : "Staked count not connected"}
          </Badge>
        )}
      </div>

      <ol
        className="flex items-stretch gap-1.5 mt-3.5 list-none p-0"
        aria-label="Supernova reward tiers"
      >
        {track.tiers.map((tier) => (
          <TierStep key={tier.key} tier={tier} />
        ))}
      </ol>

      <p className="text-[12px] text-arc-ink-3 mt-2.5 leading-[1.55]" data-testid="supernova-tier-hint">
        <TierHint track={track} stakedNote={stakedNote} loading={loading} idle={idle} />
      </p>

      <p className="text-caption text-arc-ink-4 mt-2 leading-[1.55]">
        {/* TODO(backend, supervised): the award itself — including whether the
            token-valuation minimum is met — is the staking engine's decision.
            This app shows progress and never credits anything. */}
        {TIER_VALUATION_NOTE} {TIER_DISPLAY_ONLY_NOTE} {XP_DISCLAIMER}
      </p>
    </Card>
  );
};

/**
 * One segment of the rail. `reached === null` (staked count unknown) renders
 * exactly like "not reached yet" visually but says something different to a
 * screen reader — we draw no progress we can't back up.
 */
const TierStep = ({ tier }) => {
  const reached = tier.reached === true;
  const description = [
    `${tier.name}: ${plural(tier.requiredStaked, "Supernova", "Supernovas")} staked`,
    `${fmtXp(tier.bonusXp)} XP bonus`,
    tier.reached === null
      ? "progress unknown — your staked count isn't connected"
      : reached
      ? "reached"
      : `${plural(tier.remaining, "more stake", "more stakes")} needed`,
  ].join(", ");

  return (
    <li className="flex-1 min-w-0 text-center">
      <span
        aria-hidden="true"
        className={`block h-[5px] rounded-full ${
          reached ? "bg-arc-accent" : "bg-arc-paper-2 ring-1 ring-arc-line"
        }`}
      />
      <span
        aria-hidden="true"
        className={`block text-[10.5px] font-semibold uppercase tracking-[0.04em] mt-[7px] ${
          reached ? "text-arc-accent-ink" : "text-arc-ink-4"
        }`}
      >
        {tier.name} · {fmtCount(tier.requiredStaked)} staked
      </span>
      <span
        aria-hidden="true"
        className={`block text-[11px] tnum ${reached ? "text-arc-ink-2" : "text-arc-ink-3"}`}
      >
        +{fmtXp(tier.bonusXp)} XP
      </span>
      <span className="sr-only">{description}</span>
    </li>
  );
};

/** The one line under the rail. Each branch says only what it can support. */
const TierHint = ({ track, stakedNote, loading, idle }) => {
  if (!track.known) {
    if (loading) return <>Reading your stake from Arbitrum…</>;
    if (idle) {
      return (
        <>
          Connect your wallet to see where your stake sits on these tiers. The
          tiers themselves are shown either way.
        </>
      );
    }
    return (
      <>
        {TIER_UNKNOWN_NOTE}
        {stakedNote ? <span className="text-arc-ink-4"> {stakedNote}</span> : null}
      </>
    );
  }

  if (track.topReached) {
    return (
      <>
        Top tier reached with {plural(track.stakedCount, "Supernova", "Supernovas")}{" "}
        staked.
      </>
    );
  }

  return (
    <>
      Stake {plural(track.stakesToNext, "more Supernova", "more Supernovas")} to
      reach {track.nextTier.name} ({fmtCount(track.nextTier.requiredStaked)}{" "}
      staked, +{fmtXp(track.nextTier.bonusXp)} XP bonus).
    </>
  );
};

export default SupernovaTierTrack;
